import { MSAL_INSTANCE, MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, provideAppInitializer, inject } from '@angular/core';
import { provideRouter, withComponentInputBinding, withNavigationErrorHandler } from '@angular/router';
import { AuthenticationResult, EventType, PublicClientApplication } from '@azure/msal-browser';
import { filter, firstValueFrom } from 'rxjs';
import { routes } from './app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { nexusAuthInterceptor } from './core/nexus.interceptor';
import { msalConfig } from './auth/auth.config';
import { AuthService } from './core/auth.service';
import { AuditApiService } from './core/audit-api.service';
import { reloadOnStaleChunk } from './core/chunk-reload';

export const appConfig: ApplicationConfig = {
  providers: [
    MsalService,
    MsalBroadcastService,
    provideHttpClient(withInterceptorsFromDi(), withInterceptors([nexusAuthInterceptor])),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withNavigationErrorHandler(reloadOnStaleChunk),
    ),
    provideAnimationsAsync(),
    provideNativeDateAdapter(),
    { provide: MAT_DATE_LOCALE, useValue: 'en-AU' },
    {
      provide: MSAL_INSTANCE,
      useFactory: () => {
        return new PublicClientApplication(msalConfig);
      },
    },

    provideAppInitializer(() => {
      const msalBroadcastService = inject(MsalBroadcastService);
      const msalService = inject(MsalService);
      const authService = inject(AuthService);
      const auditApiService = inject(AuditApiService);

      return (async () => {
        msalBroadcastService.msalSubject$
          .pipe(
            filter((e) => e.eventType === EventType.ACQUIRE_TOKEN_SUCCESS),
            filter((e) => e.interactionType === 'silent'),
            filter((e) => (e.payload as AuthenticationResult).fromCache === false),
          )
          .subscribe((e) => {
            const payload = e.payload as AuthenticationResult;
            auditApiService.logTokenAudit(payload.accessToken).subscribe();
          });

        let redirectResult: AuthenticationResult | null = null;

        try {
          redirectResult = await firstValueFrom(msalService.handleRedirectObservable());
        } catch (err) {
          console.error('MSAL redirect handling failed', err);
        }

        if (redirectResult?.account) {
          msalService.instance.setActiveAccount(redirectResult.account);
        } else {
          const account =
            msalService.instance.getActiveAccount() ??
            msalService.instance.getAllAccounts()[0] ??
            null;
          if (account) {
            msalService.instance.setActiveAccount(account);
          }
        }

        await authService.initialise();

        console.log('Auth + MSAL fully initialized');
      })();
    }),
  ],
};
